import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SECURITY ARCHITECTURE NOTES:
 * 
 * Token storage strategy: sessionStorage (not localStorage)
 * Reasons:
 * 1. sessionStorage is cleared when browser tab/window closes
 * 2. Not persistent across browser sessions (more secure for auth tokens)
 * 3. Not accessible from other tabs (isolation)
 * 4. Combined with httpOnly cookies for refresh tokens on the backend
 * 
 * XSS Mitigation:
 * - While sessionStorage is accessible via JS, we use short-lived access tokens
 * - Backend also sets httpOnly cookies for critical operations
 * - React's JSX auto-escapes content, preventing XSS injection
 * 
 * Future Enhancement: Move to pure httpOnly cookie-based auth if needed
 */
const TOKEN_KEY = 'ec_auth_token';

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get token from sessionStorage
  const getToken = () => sessionStorage.getItem(TOKEN_KEY);
  
  // Set token in sessionStorage and axios headers
  const setToken = (token) => {
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  useEffect(() => {
    // On mount, check for existing token and fetch user
    const token = getToken();
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // If 401, token is invalid - clear it
      if (error.response?.status === 401) {
        setToken(null);
      } else {
        console.error('Failed to fetch user:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, turnstileToken = null) => {
    const response = await axios.post(`${API}/auth/login`, { 
      email, 
      password,
      turnstile_token: turnstileToken 
    });
    const { access_token, user: userData } = response.data;
    // Store token in sessionStorage (more secure than localStorage - cleared on session end)
    // Also sets httpOnly cookie as backup on server side
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    try {
      // Call backend to clear the httpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
    }
  };

  const isAdmin = () => user?.role === 'admin';
  const isEmployee = () => user?.role === 'employee';
  const isClient = () => user?.role === 'client';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isEmployee,
      isClient,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
